import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message, Modal } from "antd";
import InvoiceNumberingSettings from "../../components/invoices/InvoiceNumberingSettings";
import AdminMobileShell from "./AdminMobileShell";
import {
  Summary, SearchBar, EmptyState, Fab, Sheet, ActionRow, DetailRow, Field,
  TextInput, TextAreaInput, SelectInput, SwitchRow, Pill, Mono, Note, Ico, fmt,
} from "../produits/productMobileKit";
import { useAuth } from "../../context/AuthContext";
import AdminPortfolioExportButtons from "../../components/AdminPortfolioExportButtons";
import { ACTIVITY_PROFILE_OPTIONS, getCompanyProfiles } from "../../utils/activityProfiles";
import {
  deleteCompany, listCompanies, listCompanyCurrencies, saveCompany, toggleCompany,
} from "./adminMobileApi";

const DEFAULT_CURRENCIES = [
  { value: "XOF", label: "Franc CFA BCEAO (XOF)" },
  { value: "EUR", label: "Euro (EUR)" },
  { value: "USD", label: "Dollar US (USD)" },
];
const COMPANY_EDIT_SETTINGS_PERMISSION =
  "Admin.GestionEntreprises.edit_settings";
const emptyForm = () => ({
  name: "", short_name: "", email: "", phone: "", website: "", address: "",
  currency: "XOF", activity_profiles: [], status: "active", light_logo: null,
});
const errorMessage = (error, fallback) => error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback;

export default function CompaniesMobile() {
  const { user, hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [query, setQuery] = useState("");
  const [detail, setDetail] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [currencies, setCurrencies] = useState(DEFAULT_CURRENCIES);
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);

  const isSuperAdmin = Boolean(user?.is_superadmin);
  const canView = isSuperAdmin || hasPermission("Admin.GestionEntreprises.view");
  const canCreate = isSuperAdmin;
  const canEdit = isSuperAdmin || hasPermission(COMPANY_EDIT_SETTINGS_PERMISSION);
  const canDelete = isSuperAdmin;

  const load = useCallback(async (nextPage = 1, append = false) => {
    if (!canView) return;
    setLoading(true);
    try {
      const result = await listCompanies(nextPage, showInactive ? "inactive" : "active");
      setRows((prev) => append ? [...prev, ...result.rows] : result.rows);
      setPage(nextPage);
      setPages(result.pages);
    } catch (error) {
      message.error(errorMessage(error, "Impossible de charger les entreprises"));
      if (!append) setRows([]);
    } finally { setLoading(false); }
  }, [canView, showInactive]);

  useEffect(() => { load(1, false); }, [load]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) => `${row.name || ""} ${row.short_name || ""} ${row.email || ""} ${row.phone || ""}`.toLowerCase().includes(needle));
  }, [query, rows]);

  const openForm = async (row = null) => {
    setEditing(row);
    setDetail(null);
    setForm(row ? {
      name: row.name || "", short_name: row.short_name || "", email: row.email || "",
      phone: row.phone || "", website: row.website || "", address: row.address || "",
      currency: row.currency_code || (typeof row.currency_id === "string" ? row.currency_id : "XOF"),
      activity_profiles: getCompanyProfiles(row), status: row.status || "active", light_logo: null,
    } : emptyForm());
    setFormOpen(true);
    if (row?.id) {
      try {
        const currencyRows = await listCompanyCurrencies(row.id);
        setCurrencies(currencyRows.length ? currencyRows.map((currency) => ({ value: currency.code, label: `${currency.name} (${currency.code})${currency.symbol ? ` · ${currency.symbol}` : ""}` })) : DEFAULT_CURRENCIES);
      } catch { setCurrencies(DEFAULT_CURRENCIES); }
    } else setCurrencies(DEFAULT_CURRENCIES);
  };

  const toggleProfile = (key) => setForm((prev) => ({
    ...prev,
    activity_profiles: prev.activity_profiles.includes(key)
      ? prev.activity_profiles.filter((item) => item !== key)
      : [...prev.activity_profiles, key],
  }));

  const submit = async () => {
    if (inFlight.current || !form.name.trim() || !form.email.trim() || !form.phone.trim()) return;
    inFlight.current = true;
    setSaving(true);
    try {
      const data = new FormData();
      data.append("name", form.name.trim());
      data.append("short_name", form.short_name.trim());
      data.append("email", form.email.trim());
      data.append("phone", form.phone.trim());
      data.append("website", form.website.trim());
      data.append("address", form.address.trim());
      if (isSuperAdmin) {
        data.append("status", form.status);
      }
      data.append("currency_id", form.currency);
      if (isSuperAdmin) {
        data.append("activity_profiles", JSON.stringify(form.activity_profiles));
      }
      if (form.light_logo instanceof File) data.append("light_logo", form.light_logo);
      await saveCompany(editing?.id, data);
      message.success(editing ? "Entreprise mise à jour" : "Entreprise créée");
      setFormOpen(false);
      setEditing(null);
      load(1, false);
    } catch (error) { message.error(errorMessage(error, "Impossible d'enregistrer l'entreprise")); }
    finally { inFlight.current = false; setSaving(false); }
  };

  const changeStatus = async (row) => {
    if (!isSuperAdmin) return;
    const next = row.status === "active" ? "inactive" : "active";
    try { await toggleCompany(row.id, next); message.success("Statut mis à jour"); setDetail(null); load(1, false); }
    catch (error) { message.error(errorMessage(error, "Mise à jour impossible")); }
  };

  const remove = (row) => Modal.confirm({
    title: "Supprimer cette entreprise ?", content: row.name, okText: "Supprimer", okType: "danger", cancelText: "Annuler", centered: true,
    onOk: async () => {
      try { await deleteCompany(row.id); message.success("Entreprise supprimée"); setDetail(null); load(1, false); }
      catch (error) { message.error(errorMessage(error, "Suppression impossible")); }
    },
  });

  if (!canView) return <AdminMobileShell title="Entreprises"><div className="body"><EmptyState icon="alert-circle" title="Accès non autorisé" /></div></AdminMobileShell>;

  return (
    <AdminMobileShell title="Entreprises" subtitle={`${fmt(rows.length)} chargée${rows.length > 1 ? "s" : ""}`}>
      <div className="appbar" style={{ borderTop: 0, paddingTop: 0 }}>
        <Summary items={[{ label: showInactive ? "Inactives" : "Actives", value: fmt(rows.length) }, { label: "Page", value: `${page}/${pages}` }]} />
        <SearchBar value={query} onChange={setQuery} placeholder="Nom, email ou téléphone…" />
        <SwitchRow label="Afficher les entreprises inactives" value={showInactive} onChange={setShowInactive} />
        {isSuperAdmin ? <AdminPortfolioExportButtons kind="companies" /> : null}
      </div>
      <div className="body">
        {loading && !rows.length ? <EmptyState loading title="Chargement…" /> : !visible.length ? <EmptyState icon="store" title="Aucune entreprise" /> : (
          <div className="list">
            {visible.map((row) => <button key={row.id} className="rcard" onClick={() => setDetail(row)}>
              <Mono name={row.name} square />
              <div className="rcard__main"><div className="rcard__name">{row.name}</div><div className="rcard__meta"><Pill tone={row.status === "active" ? "ok" : "off"} label={row.status === "active" ? "Active" : "Inactive"} /><span className="rcard__sub">{row.email || row.phone || "—"}</span></div></div>
              <span className="rcard__chev"><Ico name="chevron-right" /></span>
            </button>)}
            {page < pages ? <button className="btn btn--ghost" onClick={() => load(page + 1, true)} disabled={loading}>{loading ? "Chargement…" : "Charger la suite"}</button> : null}
          </div>
        )}
      </div>
      {canCreate ? <Fab label="Nouvelle entreprise" onClick={() => openForm()} /> : null}

      <Sheet open={Boolean(detail)} onClose={() => setDetail(null)} title={detail?.name} subtitle={detail?.short_name}>
        {detail ? <>
          <DetailRow label="Statut" value={detail.status === "active" ? "Active" : "Inactive"} />
          <DetailRow label="Email" value={detail.email || "—"} /><DetailRow label="Téléphone" value={detail.phone || "—"} />
          <DetailRow label="Site web" value={detail.website || "—"} /><DetailRow label="Adresse" value={detail.address || "—"} />
          <DetailRow label="Devise" value={detail.currency_code || detail.currency_name || "XOF"} />
          <DetailRow label="Profils" value={getCompanyProfiles(detail).map((key) => ACTIVITY_PROFILE_OPTIONS.find((item) => item.value === key)?.label || key).join(", ") || "—"} />
          {canEdit ? <ActionRow icon="pencil" label="Modifier" onClick={() => openForm(detail)} /> : null}
          {isSuperAdmin ? <ActionRow icon="rotate-ccw" label={detail.status === "active" ? "Désactiver" : "Activer"} onClick={() => changeStatus(detail)} /> : null}
          {canDelete ? <ActionRow icon="trash-2" label="Supprimer" danger onClick={() => remove(detail)} /> : null}
        </> : null}
      </Sheet>

      <Sheet open={formOpen} onClose={() => setFormOpen(false)} title={editing ? "Modifier l'entreprise" : "Nouvelle entreprise"}>
        {editing?.id && <InvoiceNumberingSettings companyId={editing.id} active={formOpen} />}
        <Field label="Nom *"><TextInput value={form.name} onChange={(name) => setForm((f) => ({ ...f, name }))} autoFocus /></Field>
        <Field label="Nom court"><TextInput value={form.short_name} onChange={(short_name) => setForm((f) => ({ ...f, short_name }))} /></Field>
        <Field label="Email *"><TextInput value={form.email} onChange={(email) => setForm((f) => ({ ...f, email }))} inputMode="email" /></Field>
        <Field label="Téléphone *"><TextInput value={form.phone} onChange={(phone) => setForm((f) => ({ ...f, phone }))} inputMode="tel" /></Field>
        <Field label="Site web"><TextInput value={form.website} onChange={(website) => setForm((f) => ({ ...f, website }))} /></Field>
        <Field label="Adresse"><TextAreaInput value={form.address} onChange={(address) => setForm((f) => ({ ...f, address }))} rows={2} /></Field>
        <Field label="Devise"><SelectInput value={form.currency} onChange={(currency) => setForm((f) => ({ ...f, currency }))} options={currencies} /></Field>
        {isSuperAdmin
          ? <Field label="Statut"><SelectInput value={form.status} onChange={(status) => setForm((f) => ({ ...f, status }))} options={[{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }]} /></Field>
          : <Note>Statut : {form.status === "active" ? "Active" : "Inactive"}. Sa mise à jour est réservée au SysAdmin.</Note>}
        {isSuperAdmin ? <>
          <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Profils d'activité</div>
          {ACTIVITY_PROFILE_OPTIONS.map((option) => <button key={option.value} type="button" className="actsheet__row" onClick={() => toggleProfile(option.value)}><Ico name={form.activity_profiles.includes(option.value) ? "check-circle-2" : "circle"} />{option.label}</button>)}
        </> : <Note>L'attribution des profils d'activité est réservée au SysAdmin.</Note>}
        <Field label="Logo clair" hint="optionnel"><div className="ctrl"><input type="file" accept="image/*" onChange={(e) => setForm((f) => ({ ...f, light_logo: e.target.files?.[0] || null }))} /></div></Field>
        {editing ? <Note>Les autres variantes de logo restent inchangées si aucun nouveau fichier n'est choisi.</Note> : null}
        <div className="sheet__actions"><button className="btn btn--ghost" onClick={() => setFormOpen(false)}>Annuler</button><button className="btn btn--primary" onClick={submit} disabled={saving || !form.name.trim() || !form.email.trim() || !form.phone.trim()}>{saving ? "Enregistrement…" : editing ? "Mettre à jour" : "Créer"}</button></div>
      </Sheet>
    </AdminMobileShell>
  );
}
